import React, { useState, useEffect } from "react";
import { privateAxios } from "../utils/axios";
import { usePermissionContext } from "../contexts/PermissionContext";
import toast from "react-hot-toast";

const defaultPermissions = [
  {
    module: "products",
    permissions: [],
  },
  {
    module: "supplier",
    permissions: [],
  },
  {
    module: "purchase",
    permissions: [],
  },
  {
    module: "expense",
    permissions: [],
  },
  {
    module: "users",
    permissions: [],
  },
  {
    module: "purchase-report",
    permissions: [],
  },
  {
    module: "expense-report",
    permissions: [],
  },
  {
    module: "products-report",
    permissions: [],
  },
  {
    module: "setting",
    permissions: [],
  },
  {
    module: "equipment-request",
    permissions: [],
  },
  {
    module: "service-request",
    permissions: [],
  },
  {
    module: "branch",
    permissions: [],
  },
];

function UpdateUser({ updateUserData, updateModalSetting, handlePageUpdate }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: "admin",
    permissions: defaultPermissions,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const { onUserUpdated } = usePermissionContext();

  useEffect(() => {
    if (updateUserData) {
      // Convert old permissions format to new format if needed
      let permissions = defaultPermissions;
      if (
        updateUserData.permissions &&
        Array.isArray(updateUserData.permissions)
      ) {
        if (
          updateUserData.permissions.length > 0 &&
          typeof updateUserData.permissions[0] === "string"
        ) {
          // Old format: ["read", "write", "delete"]
          permissions = defaultPermissions.map((module) => ({
            ...module,
            permissions: updateUserData.permissions,
          }));
        } else {
          // New format: [{module: "products", permissions: ["read", "write"]}]
          // Merge with defaultPermissions so newly added modules always appear
          const existingMap = {};
          updateUserData.permissions.forEach((p) => {
            existingMap[p.module] = p;
          });
          permissions = defaultPermissions.map((def) => {
            const found = existingMap[def.module];
            return {
              module: found ? found.module : def.module,
              permissions: found ? [...found.permissions] : [],
            };
          });
        }
      }

      setForm({
        name: updateUserData.name || "",
        phone: updateUserData.phone || "",
        email: updateUserData.email || "",
        role: updateUserData.role || "supplier",
        permissions: permissions,
        isActive: updateUserData.isActive,
      });
    }
  }, [updateUserData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "isActive") {
      setForm((prev) => ({ ...prev, isActive: checked }));
    } else if (name === "role") {
      setForm((prev) => ({
        ...prev,
        role: value,
        permissions:
          value === "admin"
            ? defaultPermissions.map((module) => ({
                ...module,
                permissions: ["read", "write", "delete"],
              }))
            : prev.permissions,
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle permission changes
  const handlePermissionChange = (moduleIndex, permission, checked) => {
    setForm((prev) => {
      const updatedPermissions = prev.permissions.map((mod, idx) => {
        if (idx !== moduleIndex) return mod;
        return {
          ...mod,
          permissions: checked
            ? [...mod.permissions, permission]
            : mod.permissions.filter((p) => p !== permission),
        };
      });
      return { ...prev, permissions: updatedPermissions };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        role: form.role,
        permissions: form.permissions,
        isActive: form.isActive,
      };

      const res = await privateAxios.put(
        `/users/${updateUserData._id}`,
        payload
      );
      if (res.data.success) {
        toast.success("User updated successfully");

        // Trigger permission refresh if needed
        await onUserUpdated(updateUserData._id);

        handlePageUpdate();
        updateModalSetting();
      } else {
        toast.error(res.data.message || "Failed to update user");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating user");
    }
    setLoading(false);
  };

  if (!updateUserData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg">
        <h2 className="text-xl font-bold mb-4">Update User</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium mb-1">Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Phone</label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>
          {/* <div>
            <label className="block font-medium mb-1">Email</label>
            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              type="email"
            />
          </div> */}
          <div>
            <label className="block font-medium mb-1">Role</label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            >
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
          <div>
            <label className="block font-medium mb-1">Permissions</label>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {form.permissions.map((module, moduleIndex) => (
                <div key={module.module} className="border rounded p-3">
                  <h4 className="font-medium mb-2 capitalize">
                    {module.module.replace("-", " ")}
                  </h4>
                  <div className="flex gap-4">
                    {["read", "write", "delete"].map((permission) => (
                      <label
                        key={permission}
                        className="flex items-center gap-1"
                      >
                        <input
                          type="checkbox"
                          checked={module.permissions.includes(permission)}
                          onChange={(e) =>
                            handlePermissionChange(
                              moduleIndex,
                              permission,
                              e.target.checked
                            )
                          }
                          disabled={false}
                        />
                        <span className="text-sm">{permission}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
              />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200"
              onClick={updateModalSetting}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-blue-600 text-white"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateUser;
